import React, {
  ReactElement,
  SyntheticEvent,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';

import { Button, FormLabel, Stack, Iframe } from 'ui/components';
import {
  SlidePanel,
  Loader,
  SearchableList,
  ListBody,
  ListSearch,
  List,
  Alert,
} from 'ui/patterns';

import {
  useGetAccountListForImportEmbedProcedures,
  useGetProcedureDetails,
} from 'hooks';

import * as Icon from 'assets/icons';

import {
  linkImportAccountList,
  ProcedureDetailsInList,
} from 'types/procedureBuilder';
import { EmbeddedProcedureProps } from './embeddedProcedureTypes';

export default function EmbeddedProcedure({
  showPanel,
  togglePanel: toggle,
  accountId,
}: EmbeddedProcedureProps): ReactElement {
  const procedureIframeRef = useRef<HTMLIFrameElement>(null);
  const tbodyRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  const [isVisible, setIsVisible] = useState(false);
  const [random, setRandom] = useState(0);
  const [keyword, setKeyword] = useState<string>('');
  const [procedureKeyword, setProcedureKeyword] = useState<string>('');
  const [activeAccount, setActiveAccount] = useState<number>();
  const [activeAccountNumber, setActiveAccountccountNumber] =
    useState<string>('');
  const [activeProcedure, setActiveProcedure] = useState<number>();
  const [activeProcedureName, setActiveProcedureName] = useState<string>('');
  const [procedureList, setProcedureList] = useState<
    Array<ProcedureDetailsInList>
  >([]);

  const {
    data: accountsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isAccountListLoading,
    isFetching,
  } = useGetAccountListForImportEmbedProcedures(accountId, keyword);

  const accountList = useMemo(() => {
    return accountsData?.pages?.map((dataItem: any) => dataItem.Data).flat();
  }, [accountsData]);

  const { data: procedureDetails, isLoading: isProcedureDetailsLoading } =
    useGetProcedureDetails({
      accountId: activeAccount,
      procedureId: activeProcedure,
    });

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  function togglePanel(
    params:
      | {
          account: string;
          procedureName: string;
        }
      | undefined,
  ) {
    setIsVisible(false);
    setTimeout(() => {
      toggle(params);
    }, 500);
  }

  const resetPanel = () => {
    togglePanel(undefined);
    setActiveAccount(undefined);
    setActiveProcedure(undefined);
    setProcedureList([]);
    setKeyword('');
    setProcedureKeyword('');
  };

  const setEmbedProcedure = (
    activeAccount: number | undefined,
    activeProcedureName: string,
  ) => {
    togglePanel({
      account: `${activeAccount}`,
      procedureName: activeProcedureName,
    });
    setActiveAccount(undefined);
    setActiveProcedure(undefined);
    setProcedureList([]);
    setKeyword('');
    setProcedureKeyword('');
  };

  const procedureActions = () => {
    return (
      <>
        <Button
          variant="secondary"
          onClick={() => {
            resetPanel();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setEmbedProcedure(activeAccount, activeProcedureName);
          }}
        >
          Save
        </Button>
      </>
    );
  };

  useEffect(() => {
    const selectedAccount = accountList?.filter(
      item => item.AccountId === activeAccount,
    );
    const accountProcedures =
      selectedAccount &&
      selectedAccount[0] &&
      selectedAccount[0].ProcedureDetails;
    if (accountProcedures) {
      const data = [...accountProcedures];
      setProcedureList(data);
    }
  }, [accountList, activeAccount]);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const handleSelectedProcedure = (procedure: ProcedureDetailsInList) => {
    setActiveProcedure(procedure?.ProcedureId);
    setActiveProcedureName(procedure?.ProcedureName);
  };

  const handleSearchKeyword = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setKeyword(value);
  };

  const handleProcedureSearch = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setProcedureKeyword(value);
    let newProcedures: Array<ProcedureDetailsInList> | undefined = [];
    const selectedAccount = accountList?.filter(
      item => item.AccountId === activeAccount,
    );
    const accountProcedures =
      selectedAccount &&
      selectedAccount[0] &&
      selectedAccount[0].ProcedureDetails;
    newProcedures =
      accountProcedures &&
      accountProcedures.filter((item: ProcedureDetailsInList) => {
        const searchTerm = `${item.ProcedureName}`;
        if (searchTerm.toLowerCase().includes(value.toLowerCase())) return item;
      });
    if (newProcedures) setProcedureList([...newProcedures]);
  };

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        resetPanel();
      }}
      size="lg"
      title="Select Procedure"
      actions={procedureActions()}
    >
      <Flex flexDirection="column" style={{ height: 'calc(100% - 48px)' }}>
        <Stack direction="x" gap={3}>
          <Box flex="1">
            <FormLabel>Accounts</FormLabel>
            <SearchableList>
              <ListSearch>
                <input
                  type="search"
                  placeholder="Search account name / CID"
                  value={keyword}
                  onChange={(e: SyntheticEvent<HTMLInputElement>) =>
                    handleSearchKeyword(e)
                  }
                />
                <Icon.Search />
              </ListSearch>
              <ListBody ref={tbodyRef}>
                {(isAccountListLoading || isFetching) && <Loader />}
                <List>
                  {accountList &&
                    accountList.map((accounts: linkImportAccountList) => (
                      <li
                        className={
                          accounts?.AccountId === activeAccount ? 'active' : ''
                        }
                        key={accounts?.AccountId}
                        title={accounts?.AccountNameAndNumber}
                        onClick={() => {
                          if (accounts) {
                            setActiveAccount(accounts.AccountId);
                            setActiveAccountccountNumber(
                              `${accounts.CID}-${accounts.SID}-${accounts.PID}`,
                            );
                          }
                        }}
                      >
                        {accounts?.AccountNameAndNumber}
                      </li>
                    ))}
                </List>
              </ListBody>
            </SearchableList>
          </Box>
          <Box flex="1">
            <FormLabel>Procedures in the selected account</FormLabel>
            <SearchableList>
              <ListSearch>
                <input
                  type="search"
                  placeholder="Search procedure names "
                  value={procedureKeyword}
                  onChange={(e: SyntheticEvent<HTMLInputElement>) =>
                    handleProcedureSearch(e)
                  }
                />
                <Icon.Search />
              </ListSearch>
              <ListBody>
                <List>
                  {procedureList &&
                    procedureList.map(procedure => (
                      <li
                        className={
                          procedure.ProcedureId === activeProcedure
                            ? 'active'
                            : ''
                        }
                        key={procedure.ProcedureId}
                        title={procedure.ProcedureName}
                        onClick={() => handleSelectedProcedure(procedure)}
                      >
                        {procedure.ProcedureName}
                      </li>
                    ))}
                </List>
              </ListBody>
            </SearchableList>
          </Box>
        </Stack>
        {activeProcedure ? (
          isProcedureDetailsLoading ? (
            <Box flex="1" mt={4} style={{ position: 'relative' }}>
              <Loader />
            </Box>
          ) : (
            <Box flex="1" mt={4} style={{ position: 'relative' }}>
              <Iframe
                ref={procedureIframeRef}
                key={random}
                srcDoc={
                  procedureDetails?.Data ? procedureDetails.Data.Content : ''
                }
              />

              <Flex
                style={{ gap: `0.5rem`, paddingBlockEnd: '1rem' }}
                justifyContent="flex-end"
              >
                <Alert
                  variant="info"
                  icon={<Icon.ExclamationCircle />}
                  style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '3px',
                  }}
                >
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="currentColor">
                      Please click the <strong>procedure refresh button</strong>{' '}
                      if the procedure is not loaded properly.
                    </Text>
                  </Flex>
                </Alert>
                <Button
                  variant="primary"
                  onClick={() => {
                    setRandom(random => random + 1);
                  }}
                  type="submit"
                  fullWidth={false}
                >
                  <Icon.Refresh title="" />
                </Button>
              </Flex>
            </Box>
          )
        ) : null}
      </Flex>
    </SlidePanel>
  );
}
